import { DoNotDisturb } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import { MenuItem } from '@mui/material';
import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import CButton from '../../components/CButton';
import CKebabBar from '../../components/CKebabBar';
import CLocalizedText from '../../components/CLocalizedText';
import CLocalizedTextStringStatic from '../../components/CLocalizedTextStringStatic';
import CModal from '../../components/CModal';
import CPageModule from '../../components/CPageModule';
import CPageModulePaginatedTable from '../../components/CPageModulePaginatedTable';
import CStatefulContainer from '../../components/CStatefulContainer';
import TagButton from '../../components/TagButton';
import CustomSelectDropdown from '../../components/general/CustomSelectDropdown';
import Page from '../../components/layout/Page';
import XDDropdown from '../../components/selection/XDDropdown';
import XDDropdownItem from '../../components/selection/XDDropdownItem';
import { ETonalButtonClassNames } from '../../enums/ETagButtonColors';
import formatTableColumn from '../../helpers/formatTableColumn';
import { updateStateAttribute, updateStateResponseStatefulAttribute, useStateExtended } from '../../helpers/helper';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import type { IAugmentedBackendResponse } from '../../models/IAugmentedBackendResponse';
import type { IRequestParams } from '../../models/IRequestParams';
import type { IResponse } from '../../models/IResponse';
import type { ISortingMeta } from '../../models/ISortingMeta';
import type { TPandasTable } from '../../models/TPandasTable';
import { SvLocalisationService } from '../../services/SvLocalisationService';
import { updateSnackbar } from '../../store/appSlice';

type TUploadMode = 'backend' | 'frontend' | 'homepage';

interface State {
  snapshot: IResponse<IAugmentedBackendResponse<TPandasTable>> & IRequestParams;
  uploadModalOpen: boolean;
  uploadMode?: TUploadMode;
  uploadFile?: File;
  uploadReqState: { isLoading: boolean; isError: boolean };
  replaceMode: boolean;
}

function getUploadSummaryMessage(
  stats: {
    changed: number;
    retained: number;
    deactivated: number;
  }
) {
  const summaryParts: string[] = [];
  const successPrefix = CLocalizedTextStringStatic('i18n.uploadSummary.success');
  const noChanges = CLocalizedTextStringStatic('i18n.uploadSummary.noChanges');
  const modifiedLabel = CLocalizedTextStringStatic('i18n.uploadSummary.modified');
  const retainedLabel = CLocalizedTextStringStatic('i18n.uploadSummary.retained');
  const deletedLabel = CLocalizedTextStringStatic('i18n.uploadSummary.deleted');

  if (stats.changed > 0) {
    summaryParts.push(`${stats.changed} ${modifiedLabel}`);
  }

  if (stats.retained > 0) {
    summaryParts.push(`${stats.retained} ${retainedLabel}`);
  }

  if (stats.deactivated > 0) {
    summaryParts.push(`${stats.deactivated} ${deletedLabel}`);
  }

  return `${successPrefix}, ${summaryParts.length > 0 ? summaryParts.join(', ') : noChanges}.`;
}

export default function PLocalisationTableView() {
  const i18nHeadingTitle = CLocalizedTextStringStatic('i18n.heading.title');
  const { locale } = useAppSelector((state) => state.dashboard);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const defaultSorting: ISortingMeta = { sortCol: 0, ascending: 'descending' };

  const [state, setState, getState] = useStateExtended<State>({
    snapshot: {
      state: { isLoading: true, isError: false },
      params: { sorting: defaultSorting }
    },
    uploadModalOpen: false,
    uploadMode: 'backend',
    uploadFile: undefined,
    uploadReqState: { isLoading: false, isError: false },
    replaceMode: false
  });

  const fetchLocalisationTable = async (page: number, sorting?: ISortingMeta, search?: string, filters?: string[]) => {
    await updateStateResponseStatefulAttribute<State, typeof state.snapshot.data>(
      { snapshot: (await getState()).snapshot },
      setState,
      async () =>
        await SvLocalisationService.getLocalisationTable({
          page,
          sorting: sorting ?? defaultSorting,
          search: search ?? '',
          filters
        })
    );

    updateStateAttribute<State>(
      {
        snapshot: {
          ...(await getState()).snapshot,
          params: {
            sorting: sorting ?? defaultSorting,
            search: search ?? '',
            filters: filters ? { options: { app_mode: filters } } : undefined
          }
        }
      },
      setState
    );
  };

  useEffect(() => {
    const params: ISortingMeta = {
      sortCol: Number.parseInt(searchParams.get('sortCol') ?? defaultSorting.sortCol.toString()),
      ascending: (searchParams.get('ascending') as ISortingMeta['ascending']) ?? 'descending'
    };

    const page = Number.parseInt(searchParams.get('page') ?? '1');
    const search = searchParams.get('search') ?? '';

    const filters = searchParams.get('filters')?.split(',');

    (async () => {
      await fetchLocalisationTable(page, params, search, filters);
    })();
  }, [locale]);

  useEffect(() => {
    console.log(state.snapshot.params?.search);
  }, [state.snapshot.params?.search]);

  const actionsLabel = CLocalizedTextStringStatic('i18n.searchText');

  const mappedResults = state.snapshot.data?.results
    ? { textKey: state.snapshot.data.results[Object.keys(state.snapshot.data.results ?? {})[0]] }
    : undefined; // Text key is the first column.

  const handleFileUpload = async () => {
    updateStateAttribute<State>({ uploadReqState: { isLoading: true, isError: false } }, setState);

    if (!state.uploadMode) return;

    const res = await SvLocalisationService.uploadI18nFile({
      mode: state.uploadMode,
      file: state.uploadFile as File,
      replaceMode: state.replaceMode
    });

    if (!(await res.isSuccess())) {
      dispatch(
        updateSnackbar({
          message: await res.getMessage(),
          severity: 'error',
          isVisible: true
        })
      );
    } else {
      const data = await res.getData();
      dispatch(
        updateSnackbar({
          message: getUploadSummaryMessage(data ?? {changed: 0, retained: 0, deactivated: 0}),
          severity: 'success',
          isVisible: true
        })
      );
    }

    updateStateAttribute<State>(
      {
        uploadModalOpen: false,
        uploadReqState: { isLoading: false, isError: false },
        uploadFile: undefined
      },
      setState
    );
  };

  return (
    <Page title={i18nHeadingTitle}>
      <CPageModule>
        <div className={'flex items-start justify-between'}>
          <h1>{i18nHeadingTitle}</h1>

          <div className={'flex items-center gap-4'}>
            <CKebabBar
              dropdownItems={
                <>
                  <XDDropdownItem
                    onClick={() => {
                      navigate('new');
                    }}
                  >
                    <AddIcon />
                    <CLocalizedText dictKey={'i18n.newEntry'} />
                  </XDDropdownItem>
                  <XDDropdownItem
                    onClick={() => {
                      updateStateAttribute<State>({ uploadModalOpen: true }, setState);
                    }}
                  >
                    <UploadIcon />
                    <CLocalizedText dictKey={'i18n.upload'} />
                  </XDDropdownItem>
                </>
              }
            >
              <span />
            </CKebabBar>

            <XDDropdown
              usePortal
              closeOnAnyClick
              button={
                <CButton style={{ borderWidth: 1 }} icons={{ start: <DownloadIcon /> }}>
                  <CLocalizedText dictKey={'i18n.downloadFileText'} />
                </CButton>
              }
            >
              <div className={'flex flex-col'}>
                <XDDropdownItem.Download type={'frontend'}>
                  <CLocalizedText dictKey={'i18n.frontend'} />
                </XDDropdownItem.Download>
                <XDDropdownItem.Download type={'backend'}>
                  <CLocalizedText dictKey={'i18n.backend'} />
                </XDDropdownItem.Download>
                <XDDropdownItem.Download type={'homepage'}>
                  <CLocalizedText dictKey={'i18n.homepage'} />
                </XDDropdownItem.Download>
              </div>
            </XDDropdown>
          </div>
        </div>
      </CPageModule>

      <CPageModule fullWidth style={{ paddingTop: 0 }}>
        <CStatefulContainer apiRequestState={state.snapshot.state} showContent={!!state.snapshot.data} submodule>
          <CPageModulePaginatedTable
            state={state.snapshot.state}
            data={
              state.snapshot.data
                ? {
                    ...state.snapshot.data,
                    results: formatTableColumn(
                      state.snapshot.data?.results ?? {},
                      0,
                      (cellContent: string, rowArray: React.ReactNode[]) => {
                        const array: React.ReactNode[] = [];
                        if (rowArray[3])
                          array.push(<TagButton color={ETonalButtonClassNames.TEAL_COMP_5}>BE</TagButton>);
                        if (rowArray[4])
                          array.push(<TagButton color={ETonalButtonClassNames.TEAL_COMP_1}>FE</TagButton>);
                        if (rowArray[5])
                          array.push(<TagButton color={ETonalButtonClassNames.TEAL_COMP_4}>HP</TagButton>);
                        if (!rowArray[6])
                          array.push(
                            <TagButton>
                              <DoNotDisturb className={'-mt-0.5'} />
                            </TagButton>
                          );
                        array.push(cellContent.split('.').join('.\u00AD'));
                        return (
                          <>
                            {array.map((e, ix) => (
                              <span key={ix}>
                                {e}
                                {ix + 1 !== array.length && <>&nbsp;&nbsp;</>}
                              </span>
                            ))}
                          </>
                        );
                      }
                    )
                  }
                : undefined
            }
            params={state.snapshot.params}
            searchBarName={'portfolioManagementPerformance'}
            searchBarPlaceholder={actionsLabel}
            colSpanArr={[4, 4, 4, 0, 0, 0, 0, 0]}
            colAlignmentArr={['left', 'left', 'left', 'left', 'left', 'left', 'left', 'left']}
            colFormatArr={[
              undefined,
              undefined,
              undefined,
              { in: 'boolean', out: 'booleanString' },
              { in: 'boolean', out: 'booleanString' },
              { in: 'boolean', out: 'booleanString' },
              undefined
            ]}
            onChange={async (page, sorting, search, filters) => {
              await fetchLocalisationTable(page, sorting, search, filters);

              const nextParams = new URLSearchParams(searchParams);
              nextParams.set('page', page.toString());
              nextParams.set('sortCol', sorting.sortCol.toString());
              nextParams.set('ascending', sorting.ascending);
              nextParams.set('search', search);

              if (filters?.length) {
                nextParams.set('filters', filters.join(','));
              } else {
                nextParams.delete('filters');
              }

              setSearchParams(nextParams);
            }}
            rowLinks={Object.values(mappedResults?.textKey ?? {}).map((e) => `${e}`)}
          />
        </CStatefulContainer>
      </CPageModule>
      {state.uploadModalOpen && (
        <CModal
          open={state.uploadModalOpen}
          onClose={() => {
            updateStateAttribute<State>({ uploadModalOpen: false }, setState);
          }}
        >
          <div className="flex flex-col gap-6">
            <h2>
              <CLocalizedText dictKey={'i18n.upload'} />
            </h2>

            {/* Mode dropdown */}
            <CustomSelectDropdown
              label={CLocalizedTextStringStatic('i18n.mode')}
              className={'capitalize'}
              value={state.uploadMode}
              onChange={(e) => {
                updateStateAttribute<State>({ uploadMode: e.target.value as any as TUploadMode }, setState);
              }}
            >
              {['backend', 'frontend', 'homepage'].map((m) => (
                <MenuItem key={m} value={m}>
                  {m}
                </MenuItem>
              ))}
            </CustomSelectDropdown>

            {/* File picker */}
            <div className="flex flex-col gap-2">
              <p className="font-bold">
                <CLocalizedText dictKey={'i18n.file'} />
              </p>

              <input
                type="file"
                accept="application/json,.json"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  updateStateAttribute<State>({ uploadFile: f }, setState);
                }}
              />

              {state.uploadFile?.name && <p className="text-sm opacity-70">{state.uploadFile.name}</p>}
            </div>

            {/* <div className="flex flex-col gap-2"> */}
            {/*  <XDCheckbox */}
            {/*    checked={state.replaceMode} */}
            {/*    onChange={(e) => { */}
            {/*      updateStateAttribute<State>({ replaceMode: e.target.checked }, setState); */}
            {/*    }} */}
            {/*  > */}
            {/*    <CLocalizedText dictKey={'i18n.replace.mode'} /> */}
            {/*  </XDCheckbox> */}
            {/* </div> */}

            {/* Upload button */}
            <div className="flex justify-end">
              <CButton
                isLoading={state.uploadReqState.isLoading}
                disabled={!state.uploadFile || state.uploadReqState.isLoading}
                icons={{ start: <UploadIcon /> }}
                onClick={handleFileUpload}
                style={{ borderWidth: '1px' }}
              >
                <CLocalizedText dictKey={'i18n.upload'} />
              </CButton>
            </div>
          </div>
        </CModal>
      )}
    </Page>
  );
}
